import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext, AuthContext } from '@/App';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const Cart = () => {
  const { cart, removeFromCart, updateCartQuantity } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    updateCartQuantity(productId, newQuantity);
  };

  const handleRemove = (productId, productName) => {
    removeFromCart(productId);
    toast.success(`${productName} retiré du panier`);
  };

  const handleCheckout = () => {
    if (!user) {
      toast.error('Veuillez vous connecter pour continuer');
      navigate('/login');
      return;
    }
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
            <ShoppingBag className="text-gray-400" size={48} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3" data-testid="empty-cart-message">Votre panier est vide</h2>
          <p className="text-gray-600 mb-8">Découvrez nos délicieux produits italiens</p>
          <Link
            to="/"
            className="inline-block bg-gradient-to-r from-slate-800 to-slate-900 text-white px-8 py-3.5 rounded-xl hover:from-slate-900 hover:to-black transition shadow-lg font-semibold"
            data-testid="continue-shopping-btn"
          >
            Continuer mes achats
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="text-4xl font-bold mb-8 text-gray-900" data-testid="cart-title">Mon Panier</h1>

        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 border border-gray-100">
          {cart.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-6 py-6 border-b last:border-b-0 border-gray-100"
              data-testid={`cart-item-${item.id}`}
            >
              <img
                src={item.image_url || 'https://via.placeholder.com/100'}
                alt={item.name}
                className="w-24 h-24 object-cover rounded-xl"
              />

              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-900" data-testid={`cart-item-name-${item.id}`}>{item.name}</h3>
                <p className="text-gray-900 font-bold mt-1" data-testid={`cart-item-price-${item.id}`}>{item.price.toFixed(2)}€</p>
              </div>

              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-1 py-1">
                <button
                  onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                  className="w-9 h-9 flex items-center justify-center hover:bg-white rounded-lg transition"
                  data-testid={`decrease-quantity-${item.id}`}
                >
                  <Minus size={16} className="text-gray-600" />
                </button>
                <span className="w-12 text-center font-semibold text-gray-900" data-testid={`cart-item-quantity-${item.id}`}>{item.quantity}</span>
                <button
                  onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                  className="w-9 h-9 flex items-center justify-center hover:bg-white rounded-lg transition"
                  data-testid={`increase-quantity-${item.id}`}
                >
                  <Plus size={16} className="text-gray-600" />
                </button>
              </div>

              <div className="text-right w-28">
                <p className="font-bold text-xl text-gray-900" data-testid={`cart-item-total-${item.id}`}>
                  {(item.price * item.quantity).toFixed(2)}€
                </p>
              </div>

              <button
                onClick={() => handleRemove(item.id, item.name)}
                className="text-gray-400 hover:text-red-600 transition p-2"
                data-testid={`remove-item-${item.id}`}
              >
                <Trash2 size={20} />
              </button>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
          <div className="flex justify-between items-center mb-8 pb-6 border-b border-gray-100">
            <span className="text-xl font-semibold text-gray-700">Total:</span>
            <span className="text-4xl font-bold text-gray-900" data-testid="cart-total">{total.toFixed(2)}€</span>
          </div>

          <button
            onClick={handleCheckout}
            className="w-full bg-gradient-to-r from-slate-800 to-slate-900 text-white py-4 rounded-xl font-semibold text-lg hover:from-slate-900 hover:to-black transition shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            data-testid="checkout-btn"
          >
            Passer la commande
          </button>

          <Link
            to="/"
            className="block text-center mt-4 text-gray-600 hover:text-gray-900 transition font-medium"
          >
            Continuer mes achats
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Cart;